import { Button, Form, Select, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useMasterData from '../hooks/useMasterData';
import useGroup from '../zustand/useGroup';
import {
  ELogFilterRangeType,
  EReportDataInterval,
  ILogReportFilter,
  IReportSummary,
} from '../screens/report/types/report-log.type';
import DateRangePicker from './DateRangePicker';
import dayjs, { Dayjs } from 'dayjs';
import useReportLog, { IAppliedReport } from '../hooks/useReportLog';
import { httpRequest } from '../helpers/api';
import {
  BaseResponsePaginationProps,
  generateQueryString,
  IHttpResponse,
} from '@qlibs/react-components';
import { DeviceProperties } from '../screens/device/types/device.type';
import { EFilterItem } from '../screens/masterReport/types/report.type';
import { useSearchParams } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';
import { canManageAssetAliasSource } from '../screens/asset/assetAliasSourceAccess';
import { initialAsset } from '../screens/asset/types/asset.type';
import {
  getTodayReportRange,
  rebaseRangeEndToTimezone,
  rebaseRangeToTimezone,
  reportDayjs,
} from '../helpers/reportRange';
import {
  MAX_BATCHED_RANGE_DAYS,
  fetchReportLogBatched,
} from '../screens/report/helpers/reportLogBatch';
import ExportDataReport from '../screens/report/components/exportDataReport';
import {
  MAX_REPORT_RANGE_DAYS,
  REPORT_INTERVAL_MAX_DAYS,
  clampReportRangeEnd,
  getReportRangeDaysInclusive,
  getReportRangeError,
  isReportRangeAllowed,
  resolveReportInterval,
} from '../screens/report/helpers/reportIntervalLimit';

type FilterReportType = {
  downloadButton?: any;
  hideFilters?: EFilterItem[];
  customDevice?: DeviceProperties;
  init?: boolean;
  isLoading?: boolean;
  onApply?: () => void;
  variant?: 'default' | 'bottomSheet';
  resetDateOnInit?: boolean;
  minuteOnly?: boolean;
};

const intervalWithoutTime = [
  EReportDataInterval.day,
  EReportDataInterval.week,
  EReportDataInterval.month,
];

const MAX_MONTHLY_MINUTE_DAYS = MAX_BATCHED_RANGE_DAYS;

/** Legacy query/filter key kept for API + URL compatibility. */
const WEEKLY_MINUTE_QUERY_KEY = 'allowWeeklyMinute';

const isWeeklyMinuteEnabled = (value: unknown) =>
  value === true || value === 'true' || value === '1';

const isMonthlyMinuteRangeAllowed = (rangeDaysInclusive: number) =>
  rangeDaysInclusive <= MAX_MONTHLY_MINUTE_DAYS;

/**
 * Auto-enable the monthly-minute API flag for opted-in companies.
 * No UI checkbox: true when interval is minute and range is within the monthly cap.
 */
const resolveAllowWeeklyMinute = (params: {
  companyEnabled: boolean;
  interval?: EReportDataInterval | string;
  rangeDaysInclusive: number;
}) =>
  params.companyEnabled &&
  params.interval === EReportDataInterval.minute &&
  isMonthlyMinuteRangeAllowed(params.rangeDaysInclusive);

const isWeeklyMinuteCompanyEnabled = (company?: {
  config?: { allowWeeklyMinuteReport?: unknown };
  group?: { name?: string | null; nickname?: string | null };
}) => {
  const groupName = `${company?.group?.name || ''} ${
    company?.group?.nickname || ''
  }`.toLowerCase();

  return (
    isWeeklyMinuteEnabled(company?.config?.allowWeeklyMinuteReport) ||
    groupName.includes('pertamina hulu mahakam') ||
    groupName.includes('phm')
  );
};

const pickWeeklyMinuteConfig = (
  ...configs: Array<{ allowWeeklyMinuteReport?: unknown } | undefined>
) =>
  configs.find((config) =>
    isWeeklyMinuteEnabled(config?.allowWeeklyMinuteReport)
  ) || configs.find(Boolean);

const pickCompanyGroup = (
  ...groups: Array<{ name?: string | null; nickname?: string | null } | undefined>
) =>
  groups.find((group) => group?.name || group?.nickname) ||
  groups.find(Boolean);

const buildSearchFilterParams = (filter: ILogReportFilter) =>
  Object.entries(filter).reduce<Record<string, string>>(
    (params, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return params;
      }

      params[key] = String(value);
      return params;
    },
    {}
  );

const normalizeDateRangeTime = (
  range: ILogReportFilter,
  timezone: number
): ILogReportFilter => ({
  ...range,
  startAt: reportDayjs(range.startAt, timezone)
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .unix(),
  endAt: reportDayjs(range.endAt, timezone)
    .set('hour', 23)
    .set('minute', 59)
    .set('second', 0)
    .unix(),
});

const normalizeMonthRange = (
  range: ILogReportFilter,
  timezone: number
): ILogReportFilter => ({
  ...range,
  interval: EReportDataInterval.month,
  startAt: reportDayjs(range.startAt, timezone)
    .startOf('month')
    .set('second', 0)
    .unix(),
  endAt: reportDayjs(range.endAt, timezone)
    .endOf('month')
    .set('hour', 23)
    .set('minute', 59)
    .set('second', 0)
    .set('millisecond', 0)
    .unix(),
});

const getRangeDaysInclusive = getReportRangeDaysInclusive;

/**
 * Keeps the interval while the range allows it, else takes the finest one
 * that does. A range longer than any interval allows is cut from its start.
 */
const fitFilterToIntervalLimit = (
  range: ILogReportFilter,
  timezone: number,
  minuteOnly: boolean
): ILogReportFilter => {
  const maxDays = minuteOnly
    ? REPORT_INTERVAL_MAX_DAYS[EReportDataInterval.minute]!
    : MAX_REPORT_RANGE_DAYS;
  const endAt = clampReportRangeEnd(
    range.startAt,
    range.endAt,
    timezone,
    maxDays
  );
  const rangeDays = getRangeDaysInclusive(range.startAt, endAt, timezone);
  const interval = minuteOnly
    ? EReportDataInterval.minute
    : isReportRangeAllowed(range.interval, rangeDays)
    ? range.interval
    : resolveReportInterval(rangeDays);

  if (endAt === range.endAt && interval === range.interval) {
    return range;
  }

  const nextFilter = { ...range, endAt, interval };

  return intervalWithoutTime.includes(interval)
    ? normalizeDateRangeTime(nextFilter, timezone)
    : nextFilter;
};

const FilterReport: React.FC<FilterReportType> = ({
  downloadButton,
  hideFilters,
  customDevice,
  init,
  isLoading,
  onApply,
  variant = 'default',
  resetDateOnInit = false,
  minuteOnly = false,
}) => {
  const [form] = Form.useForm();
  const {
    company,
    setCompany,
    asset,
    setAsset,
    filter,
    setFilter,
    setDevices,
    timezone,
    browserTimezone,
    syncBrowserTimezone,
  } = useGroup();
  const {
    getListCompanies,
    getListAssets,
    companies,
    assets,
    loadingCompany,
    loadingAsset,
    isOperationMode,
    checkOperationMode,
    loadingOperationMode,
    setIsOperationMode,
  } = useMasterData();
  const {
    setShowReportTableConfig,
    setDataCompletion,
    setLoadingLog,
    setLogs,
    setReportSummary,
    setAppliedReport,
    loadingLog,
    loadingDevice,
    setLiterUnit,
    setLoadingDevice,
    setEngineEvents,
    setClutchEvents,
  } = useReportLog();
  const [loadingChangeAsset, setLoadingChangeAsset] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuthUser();
  const isCustomDeviceMode = Boolean(customDevice?.deviceId);
  const weeklyMinuteRoleEnabled = canManageAssetAliasSource(auth()?.role);

  const resolveFilter = useCallback(
    (baseFilter?: Partial<ILogReportFilter>) => {
      const nextFilter = {
        ...filter,
        ...(baseFilter || {}),
      } as ILogReportFilter;

      if (!isCustomDeviceMode || !customDevice) {
        return {
          ...nextFilter,
          timezone,
        };
      }

      return {
        ...nextFilter,
        assetId: customDevice.assetId,
        deviceType: customDevice.deviceType.name.toLowerCase(),
        deviceId: customDevice.deviceId,
        timezone,
      };
    },
    [customDevice, filter, isCustomDeviceMode, timezone]
  );

  const syncFilter = useCallback(
    (baseFilter?: Partial<ILogReportFilter>) => {
      const nextFilter = resolveFilter(baseFilter);
      setFilter(nextFilter);
      return nextFilter;
    },
    [resolveFilter, setFilter]
  );

  const filterAssetOptions = useMemo(() => {
    const data = assets.map((item) => ({
      value: item.assetId,
      label: item.name,
    }));
    return [...data];
  }, [assets]);

  const filterCompanyOptions = useMemo(() => {
    const data = companies.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));
    return [...data];
  }, [companies]);

  const activeCompany = useMemo(() => {
    const companyFromList = companies.find(
      (item) => item.companyId === company.companyId
    );
    const companyFromAsset =
      asset.companyId === company.companyId ? asset.company : undefined;

    return {
      ...(company || {}),
      ...(companyFromList || {}),
      ...(companyFromAsset || {}),
      config: pickWeeklyMinuteConfig(
        companyFromList?.config,
        companyFromAsset?.config,
        company?.config
      ),
      group: pickCompanyGroup(
        companyFromList?.group,
        companyFromAsset?.group,
        company?.group
      ),
    };
  }, [asset.company, asset.companyId, companies, company]);

  const weeklyMinuteCompanyEnabled = useMemo(
    () =>
      isWeeklyMinuteCompanyEnabled(activeCompany) || weeklyMinuteRoleEnabled,
    [activeCompany, weeklyMinuteRoleEnabled]
  );

  // const calculateRange = (type: string) => {
  //   switch (type) {
  //     case ELogFilterRangeType.last_30_day:
  //       return [moment().subtract(30, 'days'), moment()];
  //     case ELogFilterRangeType.last_month:
  //       return [
  //         moment().subtract(1, 'months').startOf('month'),
  //         moment().subtract(1, 'months').endOf('month'),
  //       ];
  //     case ELogFilterRangeType.last_7_day:
  //       return [moment().subtract(7, 'days'), moment()];
  //     case ELogFilterRangeType.today:
  //       return [moment().startOf('day'), moment().endOf('day')];
  //     case ELogFilterRangeType.last_day:
  //       return [
  //         moment().subtract(1, 'days').startOf('day'),
  //         moment().subtract(1, 'days').endOf('day'),
  //       ];
  //     default:
  //       return undefined;
  //   }
  // };

  // const handleRangeTypeChange = (rangeType: string) => {
  //   const range = calculateRange(rangeType);
  //   form.setFieldsValue({
  //     rangeType,
  //     range,
  //   });
  // };

  const handleChangeAsset = async (assetId: string) => {
    try {
      await handleOnChangeAsset(assetId);
    } catch (error) {
      console.error("failed change asset", error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isCustomDeviceMode) {
      getListCompanies();
    }

    form.setFieldsValue({
      rangeType: ELogFilterRangeType.today,
      interval: filter.interval,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isCustomDeviceMode && company.companyId) {
      getListAssets({ filterCompanyIds: company.companyId });
    }
  }, [company.companyId, isCustomDeviceMode]);

  useEffect(() => {
    if (isCustomDeviceMode || assets.length === 0) {
      return;
    }

    const queryAssetId = searchParams.get("assetId");

    if (queryAssetId) {
      const selectedAsset = assets.find(
        (item) => item.assetId === queryAssetId,
      );

      if (selectedAsset) {
        form.setFieldsValue({
          asset: selectedAsset.assetId,
        });
        setAsset(selectedAsset);
      }

      return;
    }

    const lastSelectedAsset = assets.find(
      (item) => item.assetId === asset.assetId,
    );

    if (lastSelectedAsset) {
      form.setFieldsValue({
        asset: lastSelectedAsset.assetId,
      });
      if (filter.assetId !== lastSelectedAsset.assetId) {
        handleChangeAsset(lastSelectedAsset.assetId);
      }
      return;
    }

    const firstAsset = assets[0];
    form.setFieldsValue({
      asset: firstAsset.assetId,
    });
    setAsset(firstAsset);
    handleChangeAsset(firstAsset.assetId);
  }, [assets, isCustomDeviceMode, searchParams]);

  useEffect(() => {
    if (company) {
      form.setFieldsValue({
        company: !loadingCompany ? company.companyId : undefined,
      });
    }
    if (asset) {
      form.setFieldsValue({
        asset: !loadingAsset ? asset.assetId : undefined,
      });
    }
  }, [company, loadingCompany, asset, loadingAsset]);

  const actionDisabled = useMemo(() => {
    if (
      loadingAsset ||
      loadingCompany ||
      loadingLog ||
      loadingDevice ||
      isLoading ||
      loadingChangeAsset
    ) {
      return true;
    }
    return false;
  }, [
    loadingCompany,
    loadingAsset,
    loadingLog,
    loadingDevice,
    isLoading,
    loadingChangeAsset,
  ]);

  function handleOnChangeCompany(companyId: string) {
    const found = companies.find((item) => item.companyId === companyId);
    if (found) {
      setCompany(found);
      setAsset(initialAsset);
      form.setFieldsValue({
        asset: undefined,
      });

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('assetId');
      newSearchParams.delete('deviceType');
      setSearchParams(newSearchParams);
    }
  }

  async function handleOnChangeAsset(assetId: string) {
    if (isCustomDeviceMode) {
      return;
    }

    try {
      setLoadingChangeAsset(true);
      const found = assets.find((item) => item.assetId === assetId);
      if (found) {
        setAsset(found);

        const res = await httpRequest.get<
          BaseResponsePaginationProps<DeviceProperties>
        >(
          `/device${generateQueryString({
            filterAssetIds: found.assetId,
            filterStatus: 'active',
          })}`
        );

        const results = res.data.payload.results;

        setDevices(results);
        const resDevices = results.map((item) =>
          item.deviceType.name.toLocaleLowerCase()
        );

        setFilter({
          ...filter,
          deviceType: [...new Set(resDevices)].join(','),
          deviceId: undefined,
          assetId: found.assetId,
        });
      }
    } catch (error) {
      console.error('failed change asset', error);
    } finally {
      setLoadingChangeAsset(false);
    }
  }

  function handleChangeInterval(e: string) {
    const interval = e as EReportDataInterval;
    const allowWeeklyMinute = resolveAllowWeeklyMinute({
      companyEnabled: weeklyMinuteCompanyEnabled,
      interval,
      rangeDaysInclusive,
    });
    const nextFilter = {
      ...filter,
      interval,
      allowWeeklyMinute,
    };

    if (interval === EReportDataInterval.month) {
      syncFilter(normalizeMonthRange(nextFilter, timezone));
      return;
    }

    syncFilter(
      intervalWithoutTime.includes(interval)
        ? normalizeDateRangeTime(nextFilter, timezone)
        : nextFilter
    );
  }

  function handlOnChangeCustomTime(date: Dayjs, type: string) {
    let startAt = filter.startAt;
    let endAt = filter.endAt;

    if (type === 'start') {
      startAt = reportDayjs(startAt, timezone)
        .set('hour', date.get('hour'))
        .set('minute', date.get('minute'))
        .set('second', 0)
        .unix();
    }

    if (type === 'end') {
      endAt = reportDayjs(endAt, timezone)
        .set('hour', date.get('hour'))
        .set('minute', date.get('minute'))
        .set('second', 0)
        .unix();
    }

    syncFilter({
      ...filter,
      startAt,
      endAt,
      allowWeeklyMinute: resolveAllowWeeklyMinute({
        companyEnabled: weeklyMinuteCompanyEnabled,
        interval: filter.interval,
        rangeDaysInclusive: getRangeDaysInclusive(startAt, endAt, timezone),
      }),
    });
  }

  function handleOnChangeCustomDateRange(dates: any) {
    let newFilter: ILogReportFilter = { ...filter };
    let date1: number = dayjs().get('date');
    let date2: number = dayjs().get('date');

    if (filter.interval === EReportDataInterval.month) {
      if (!dates?.[0] || !dates?.[1]) {
        return;
      }

      syncFilter(
        normalizeMonthRange(
          {
            ...newFilter,
            startAt: reportDayjs(newFilter.startAt, timezone)
              .set('year', dayjs(dates[0]).get('year'))
              .set('month', dayjs(dates[0]).get('month'))
              .startOf('month')
              .unix(),
            endAt: reportDayjs(newFilter.endAt, timezone)
              .set('year', dayjs(dates[1]).get('year'))
              .set('month', dayjs(dates[1]).get('month'))
              .endOf('month')
              .set('hour', 23)
              .set('minute', 59)
              .set('second', 0)
              .set('millisecond', 0)
              .unix(),
            interval: EReportDataInterval.month,
          },
          timezone
        )
      );
      return;
    }

    if (dates[0]) {
      date1 = dayjs(dates[0]).get('date');
      const month = dayjs(dates[0]).get('month');
      const year = dayjs(dates[0]).get('year');
      if (!intervalWithoutTime.includes(filter.interval)) {
        newFilter = {
          ...newFilter,
          startAt: reportDayjs(newFilter.startAt, timezone)
            .set('date', date1)
            .set('month', month)
            .set('year', year)
            .set('second', 0)
            .unix(),
        };
      } else {
        newFilter = {
          ...newFilter,
          startAt: reportDayjs(newFilter.startAt, timezone)
            .set('date', date1)
            .set('month', month)
            .set('year', year)
            .set('hour', 0)
            .set('minute', 0)
            .set('second', 0)
            .unix(),
        };
      }
    }

    if (dates[1]) {
      date2 = dayjs(dates[1]).get('date');
      const month = dayjs(dates[1]).get('month');
      const year = dayjs(dates[1]).get('year');
      if (!intervalWithoutTime.includes(filter.interval)) {
        newFilter = {
          ...newFilter,
          endAt: reportDayjs(newFilter.endAt, timezone)
            .set('date', date2)
            .set('month', month)
            .set('year', year)
            .set('second', 0)
            .unix(),
        };
      } else {
        newFilter = {
          ...newFilter,
          endAt: reportDayjs(newFilter.endAt, timezone)
            .set('date', date2)
            .set('month', month)
            .set('year', year)
            .set('hour', 23)
            .set('minute', 59)
            .set('second', 0)
            .unix(),
        };
      }
    }

    const nextFilter = fitFilterToIntervalLimit(newFilter, timezone, minuteOnly);

    if (nextFilter.endAt !== newFilter.endAt) {
      console.log(
        `Range is limited to ${getRangeDaysInclusive(
          nextFilter.startAt,
          nextFilter.endAt,
          timezone
        )} days`
      );
    }

    form.setFieldsValue({
      interval: nextFilter.interval,
    });

    syncFilter({
      ...nextFilter,
      allowWeeklyMinute: resolveAllowWeeklyMinute({
        companyEnabled: weeklyMinuteCompanyEnabled,
        interval: nextFilter.interval,
        rangeDaysInclusive: getRangeDaysInclusive(
          nextFilter.startAt,
          nextFilter.endAt,
          timezone
        ),
      }),
    });
  }

  const rangeDaysInclusive = useMemo(() => {
    return getRangeDaysInclusive(filter.startAt, filter.endAt, timezone);
  }, [filter.startAt, filter.endAt]);

  const intervalOptions = useMemo(
    () => [
      {
        label: 'Minute',
        value: EReportDataInterval.minute,
        disabled: !isReportRangeAllowed(
          EReportDataInterval.minute,
          rangeDaysInclusive
        ),
      },
      {
        label: 'Hour',
        value: EReportDataInterval.hour,
        disabled:
          minuteOnly ||
          !isReportRangeAllowed(EReportDataInterval.hour, rangeDaysInclusive),
      },
      {
        label: 'Day',
        value: EReportDataInterval.day,
        disabled:
          minuteOnly ||
          !isReportRangeAllowed(EReportDataInterval.day, rangeDaysInclusive),
      },
      // temporarily disabled
      // {
      //   label: 'Month',
      //   value: EReportDataInterval.month,
      //   disabled: true,
      // },
    ],
    [minuteOnly, rangeDaysInclusive]
  );

  useEffect(() => {
    const nextAllowWeeklyMinute = resolveAllowWeeklyMinute({
      companyEnabled: weeklyMinuteCompanyEnabled,
      interval: filter.interval,
      rangeDaysInclusive,
    });

    if (Boolean(filter.allowWeeklyMinute) !== nextAllowWeeklyMinute) {
      syncFilter({
        ...filter,
        allowWeeklyMinute: nextAllowWeeklyMinute,
      });
      return;
    }

    const nextFilter = fitFilterToIntervalLimit(filter, timezone, minuteOnly);

    if (nextFilter === filter) {
      return;
    }

    form.setFieldsValue({
      interval: nextFilter.interval,
    });
    syncFilter({ ...nextFilter, allowWeeklyMinute: false });
  }, [
    filter,
    form,
    minuteOnly,
    rangeDaysInclusive,
    syncFilter,
    timezone,
    weeklyMinuteCompanyEnabled,
  ]);

  // useEffect(() => {
  //   let ignore = false;
  //   async function fetchDevices(params: any) {
  //     try {
  //       console.log('meredith - fetch device here 2');
  //       setLoadingDevice(true);
  //       const res = await httpRequest.get<
  //         BaseResponsePaginationProps<DeviceProperties>
  //       >(`/device${generateQueryString(params)}`);

  //       if (!ignore && res.data.payload.results) {
  //         const result = res.data.payload.results;
  //         setDevices(result);
  //         const resDevices = result.map((item) =>
  //           item.deviceType.name.toLocaleLowerCase()
  //         );
  //         const currentDeviceTypes = [...new Set(resDevices)].join(',');
  //         setFilter({
  //           ...filter,
  //           deviceType: currentDeviceTypes,
  //         });
  //       }
  //     } catch (error) {
  //       console.error(`failed fetch devices`, error);
  //     } finally {
  //       setLoadingDevice(false);
  //     }
  //   }

  //   if (!customDevice) {
  //     fetchDevices({
  //       filterAssetIds: filter.assetId,
  //       filterStatus: 'active',
  //     });
  //   }

  //   return () => {
  //     ignore = true;
  //   };
  // }, [filter.assetId, customDevice]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const logRequestIdRef = useRef(0);
  const engineEventsRequestIdRef = useRef(0);
  const clutchEventsRequestIdRef = useRef(0);
  const deviceSyncRequestIdRef = useRef(0);

  const getLogReport = async (queryFilter?: ILogReportFilter) => {
    const requestId = ++logRequestIdRef.current;
    let appliedReport: IAppliedReport | null = null;

    try {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const { signal } = controller;

      setLoadingLog(true);
      const currentFilter = resolveFilter(queryFilter);
      // what the data are calculated with until the next Apply
      const { asset: requestAsset, devices: requestDevices } =
        useGroup.getState();
      appliedReport = {
        filter: currentFilter,
        timezone,
        asset: requestAsset,
        devices: requestDevices,
      };
      const rangeError = getReportRangeError({
        interval: currentFilter.interval,
        startAt: Number(currentFilter.startAt),
        endAt: Number(currentFilter.endAt),
        timezone,
      });

      if (rangeError) {
        message.error(rangeError);
        return;
      }

      let isIncludeOpMode = false;

      if (!isCustomDeviceMode) {
        const resOPMode = await httpRequest.get<
          IHttpResponse<{ isExist: boolean }>
        >(`/operation-mode/${currentFilter.assetId}/check-by-asset`, {
          signal,
        });

        if (signal.aborted || requestId !== logRequestIdRef.current) {
          return;
        }
        if (resOPMode && resOPMode.data && resOPMode.data.payload) {
          if (requestId === logRequestIdRef.current) {
            setIsOperationMode(resOPMode.data.payload.isExist);
          }
          isIncludeOpMode = resOPMode.data.payload.isExist;
        }
      }

      const newFilter = {
        ...currentFilter,
        deviceType:
          !isCustomDeviceMode && isIncludeOpMode
            ? [currentFilter.deviceType, "operation_mode"]
                .filter(Boolean)
                .join(",")
            : currentFilter.deviceType,
        deviceId: isCustomDeviceMode ? currentFilter.deviceId : undefined,
      };

      const requestParams = {
        ...newFilter,
        allowWeeklyMinute: resolveAllowWeeklyMinute({
          companyEnabled: weeklyMinuteCompanyEnabled,
          interval: newFilter.interval,
          rangeDaysInclusive: getRangeDaysInclusive(
            Number(newFilter.startAt),
            Number(newFilter.endAt),
            timezone
          ),
        })
          ? true
          : undefined,
      };

      if (signal.aborted || requestId !== logRequestIdRef.current) {
        return;
      }

      // the summary is calculated by the database over the minutes of the
      // range, without it the summary is calculated from the logs
      const [newResults, newSummary] = await Promise.all([
        fetchReportLogBatched({
          query: requestParams,
          startAt: Number(requestParams.startAt),
          endAt: Number(requestParams.endAt),
          batch: requestParams.interval === EReportDataInterval.minute,
          signal,
        }),
        httpRequest
          .get<IHttpResponse<IReportSummary | null>>(
            `/report-log/summary${generateQueryString({
              assetId: requestParams.assetId,
              deviceType: requestParams.deviceType,
              deviceId: requestParams.deviceId,
              interval: requestParams.interval,
              startAt: requestParams.startAt,
              endAt: requestParams.endAt,
              timezone,
            })}`,
            { signal },
          )
          .then((res) => res?.data?.payload || null)
          .catch((error) => {
            if (signal.aborted) throw error;
            console.error("failed get report summary", error);
            return null;
          }),
      ]);

      if (signal.aborted || requestId !== logRequestIdRef.current) {
        return;
      }

      if (newFilter.interval === "minute") {
        if (requestId === logRequestIdRef.current) {
          setLiterUnit("Liter/Min");
        }
      } else {
        if (requestId === logRequestIdRef.current) {
          setLiterUnit("Liter/H");
        }
      }

      if (
        !abortControllerRef.current?.signal.aborted &&
        requestId === logRequestIdRef.current
      ) {
        setLogs(newResults);
        setAppliedReport(appliedReport);
        setReportSummary(newSummary);
      }

      // console.log('meredith - result', newResults);
    } catch (error: any) {
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
        return;
      }

      console.error("failed get report log", error);
      if (requestId === logRequestIdRef.current) {
        setDataCompletion([]);
        setLogs({});
        setAppliedReport(appliedReport);
      }
    } finally {
      if (requestId === logRequestIdRef.current) {
        setLoadingLog(false);
      }
    }
  };

  const fetchEngineEvents = async (queryFilter?: ILogReportFilter) => {
    const requestId = ++engineEventsRequestIdRef.current;

    try {
      const currentFilter = resolveFilter(queryFilter);
      const res = await httpRequest.get<IHttpResponse<any>>(
        '/report-log/engine-events',
        {
          params: {
            assetId: currentFilter.assetId,
            startAt: currentFilter.startAt,
            endAt: currentFilter.endAt,
            interval: currentFilter.interval,
            dataSource: currentFilter.dataSource || 1,
            timezone: currentFilter.timezone || 7,
            deviceType: 1,
          },
        },
      );
      if (requestId !== engineEventsRequestIdRef.current) {
        return;
      }

      if (res?.data?.payload) {
        setEngineEvents(res.data.payload);
      } else {
        setEngineEvents(null);
      }
    } catch (error) {
      if (requestId !== engineEventsRequestIdRef.current) {
        return;
      }

      console.error("Failed to fetch engine events:", error);
      setEngineEvents(null);
    }
  };

  const fetchClutchEvents = async (queryFilter?: ILogReportFilter) => {
    const requestId = ++clutchEventsRequestIdRef.current;

    try {
      const currentFilter = resolveFilter(queryFilter);
      const res = await httpRequest.get<IHttpResponse<any>>(
        '/report-log/clutch-events',
        {
          params: {
            assetId: currentFilter.assetId,
            startAt: currentFilter.startAt,
            endAt: currentFilter.endAt,
            interval: currentFilter.interval,
            dataSource: currentFilter.dataSource || 1,
            timezone: currentFilter.timezone || 7,
            deviceType: 1,
          },
        },
      );
      if (requestId !== clutchEventsRequestIdRef.current) {
        return;
      }

      if (res?.data?.payload) {
        setClutchEvents(res.data.payload);
      } else {
        setClutchEvents(null);
      }
    } catch (error) {
      if (requestId !== clutchEventsRequestIdRef.current) {
        return;
      }

      console.error("Failed to fetch clutch events:", error);
      setClutchEvents(null);
    }
  };

  const syncDevicesForAsset = async (assetId: string) => {
    const requestId = ++deviceSyncRequestIdRef.current;

    try {
      const res = await httpRequest.get<
        BaseResponsePaginationProps<DeviceProperties>
      >(
        `/device${generateQueryString({
          filterAssetIds: assetId,
          filterStatus: "active",
        })}`,
      );

      if (requestId !== deviceSyncRequestIdRef.current) {
        return;
      }

      const result = res.data.payload.results;
      setDevices(result);
      return result;
    } catch (error) {
      if (requestId !== deviceSyncRequestIdRef.current) {
        return;
      }

      console.error("failed sync devices for report filter", error);
    }
  };

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  const prevTimezoneRef = useRef(timezone);

  useEffect(() => {
    if (prevTimezoneRef.current !== timezone && prevTimezoneRef.current !== undefined) {
      const previousTimezone = prevTimezoneRef.current;
      const updatedFilter = syncFilter({
        ...filter,
        startAt: rebaseRangeToTimezone(
          filter.startAt,
          previousTimezone,
          timezone
        ),
        endAt: rebaseRangeEndToTimezone(
          filter.endAt,
          previousTimezone,
          timezone
        ),
        timezone: timezone,
      });
      prevTimezoneRef.current = timezone;

      if (init && filter.assetId && filter.deviceType && !loadingLog) {
        getLogReport(updatedFilter);
        fetchEngineEvents(updatedFilter);
        fetchClutchEvents(updatedFilter);
      }
    } else if (prevTimezoneRef.current === undefined && timezone !== undefined) {
      prevTimezoneRef.current = timezone;
    }
  }, [filter, init, loadingLog, syncFilter, timezone]);

  useEffect(() => {
    const checkDeviceTimezone = () => {
      const currentDeviceTz = new Date().getTimezoneOffset() / -60;

      if (currentDeviceTz !== browserTimezone) {
        message.info({
          content: `Device timezone changed to GMT+${currentDeviceTz}. Data will be refreshed.`,
          duration: 3,
        });

        syncBrowserTimezone(currentDeviceTz);
      }
    };

    const interval = setInterval(checkDeviceTimezone, 10000);

    return () => clearInterval(interval);
  }, [browserTimezone, syncBrowserTimezone]);

  // const getLogReport2 = async (queryFilter?: any) => {
  //   try {
  //     setLoadingLog(true);
  //     let isIncludeOpMode = false;
  //     let newFilter;

  //     if (!customDevice) {
  //       const resOPMode = await httpRequest.get<
  //         IHttpResponse<{ isExist: boolean }>
  //       >(`/operation-mode/${filter.assetId}/check-by-asset`);

  //       if (resOPMode && resOPMode.data && resOPMode.data.payload) {
  //         setIsOperationMode(resOPMode.data.payload.isExist);
  //         isIncludeOpMode = resOPMode.data.payload.isExist;
  //       }
  //     }

  //     if (queryFilter) {
  //       console.log('meredith filter here 1', queryFilter);
  //       newFilter = {
  //         ...queryFilter,
  //         deviceType: customDevice
  //           ? customDevice.deviceType.name.toLowerCase()
  //           : isIncludeOpMode
  //           ? queryFilter.deviceType.concat(',operation_mode')
  //           : queryFilter.deviceType,
  //         deviceId: customDevice ? queryFilter.deviceId : undefined,
  //       };
  //     } else {
  //       console.log('meredith filter here 2');
  //       newFilter = {
  //         ...filter,
  //         deviceType: customDevice
  //           ? customDevice.deviceType.name.toLowerCase()
  //           : isIncludeOpMode
  //           ? filter.deviceType.concat(',operation_mode')
  //           : filter.deviceType,
  //         deviceId: customDevice ? filter.deviceId : undefined,
  //       };
  //     }

  //     console.log('meredith - filter 2', newFilter);

  //     const intervalInSeconds = 8 * 3600;
  //     let currentTimestamp = filter.startAt;
  //     let endTimestamp = filter.endAt;
  //     const promises = [];

  //     while (currentTimestamp < endTimestamp) {
  //       const nextTimestamp = Math.min(
  //         currentTimestamp + intervalInSeconds,
  //         endTimestamp
  //       );

  //       promises.push(
  //         await httpRequest
  //           .get<IHttpResponse<ILogReport>>(
  //             `report-log${generateQueryString({
  //               ...newFilter,
  //               startAt: currentTimestamp,
  //               endAt: nextTimestamp,
  //             })}`
  //           )
  //           .then((res) => {
  //             return res.data.payload;
  //           })
  //       );
  //       currentTimestamp = nextTimestamp;
  //     }

  //     const result = Promise.all(promises);

  //     result.then((results) => {
  //       let newResults: any = {};
  //       if (results && results.length > 0) {
  //         results.forEach((res) => {
  //           if (res.flowmeter) {
  //             newResults = {
  //               ...newResults,
  //               flowmeter: {
  //                 ...newResults.flowmeter,
  //                 ...res.flowmeter,
  //               },
  //             };
  //           }

  //           if (res.rpm) {
  //             newResults = {
  //               ...newResults,
  //               rpm: {
  //                 ...newResults.rpm,
  //                 ...res.rpm,
  //               },
  //             };
  //           }

  //           if (res.ae) {
  //             newResults = {
  //               ...newResults,
  //               ae: {
  //                 ...newResults.ae,
  //                 ...res.ae,
  //               },
  //             };
  //           }

  //           if (res.gps) {
  //             newResults = {
  //               ...newResults,
  //               gps: {
  //                 ...newResults.gps,
  //                 ...res.gps,
  //               },
  //             };
  //           }

  //           if (res.operation_mode) {
  //             newResults = {
  //               ...newResults,
  //               operation_mode: {
  //                 ...newResults.operation_mode,
  //                 ...res.operation_mode,
  //               },
  //             };
  //           }
  //         });
  //       }

  //       console.log('meredith - result', newResults);
  //       setLogs(newResults);
  //     });
  //   } catch (error) {
  //     console.error('failed get report log', error);
  //     setDataCompletion([]);
  //     setLogs({});
  //   } finally {
  //     setLoadingLog(false);
  //   }
  // };

  async function onApplyFilter() {
    const currentFilter = syncFilter(filter);

    if (isCustomDeviceMode) {
      setSearchParams({
        tab: '2',
      });
    } else {
      setSearchParams(buildSearchFilterParams(currentFilter));
    }

    getLogReport(currentFilter);
    fetchEngineEvents(currentFilter);
    fetchClutchEvents(currentFilter);
  }

  async function handleFetchInitData() {
    try {
      if (isCustomDeviceMode) {
        const currentFilter = syncFilter(filter);
        await getLogReport(currentFilter);
        fetchEngineEvents(currentFilter);
        fetchClutchEvents(currentFilter);
        return;
      }

      if (!filter.assetId) {
        return;
      }

      const selectedAsset =
        assets.find((item) => item.assetId === filter.assetId) ||
        (asset.assetId === filter.assetId ? asset : undefined);

      const res = await httpRequest.get<
        BaseResponsePaginationProps<DeviceProperties>
      >(
        `/device${generateQueryString({
          filterAssetIds:
            selectedAsset?.aliasId || selectedAsset?.assetId || filter.assetId,
          filterStatus: 'active',
        })}`
      );

      const result = res.data.payload.results;
      setDevices(result);
      const resDevices = result.map((item) =>
        item.deviceType.name.toLocaleLowerCase()
      );

      const currentFilter = fitFilterToIntervalLimit(
        {
          ...filter,
          ...(resetDateOnInit
            ? {
                ...getTodayReportRange(timezone),
                interval: EReportDataInterval.minute,
              }
            : {}),
          deviceType: [...new Set(resDevices)].join(','),
          deviceId: undefined,
          timezone: timezone,
        },
        timezone,
        minuteOnly
      );

      setFilter(currentFilter);
      await getLogReport(currentFilter);
      fetchEngineEvents(currentFilter);
      fetchClutchEvents(currentFilter);
    } catch (error) {
      console.error('failed get data device type', error);
    }
  }

  async function handleFetchDataFromQuery() {
    if (isCustomDeviceMode) {
      await handleFetchInitData();
      return;
    }

    const queryFilter: any = {};
    searchParams.forEach((value, name) => {
      if (name.includes('interval') && value.toLowerCase() === 'hour') {
        setLiterUnit('Liter/H');
      }

      if (['timezone', 'startAt', 'endAt'].includes(name)) {
        queryFilter[name] = Number(value);
      } else if (name === WEEKLY_MINUTE_QUERY_KEY) {
        queryFilter[name] = isWeeklyMinuteEnabled(value);
      } else {
        queryFilter[name] = value;
      }
    });

    const selectedAsset =
      assets.find((item) => item.assetId === queryFilter.assetId) ||
      (asset.assetId === queryFilter.assetId ? asset : undefined);

    const queryCurrentFilter = {
      ...queryFilter,
      deviceId: undefined,
      timezone: timezone,
    };

    const queryCompany =
      companies.find((item) => item.companyId === company.companyId) || company;
    const queryWeeklyMinuteEnabled =
      isWeeklyMinuteCompanyEnabled(queryCompany) || weeklyMinuteRoleEnabled;
    const fittedQueryFilter = fitFilterToIntervalLimit(
      queryCurrentFilter,
      timezone,
      minuteOnly
    );
    const safeCurrentFilter = {
      ...fittedQueryFilter,
      allowWeeklyMinute: resolveAllowWeeklyMinute({
        companyEnabled: queryWeeklyMinuteEnabled,
        interval: fittedQueryFilter.interval,
        rangeDaysInclusive: getRangeDaysInclusive(
          fittedQueryFilter.startAt,
          fittedQueryFilter.endAt,
          timezone
        ),
      }),
    };

    let assetDevices: DeviceProperties[] | undefined;

    if (selectedAsset?.devices?.length) {
      deviceSyncRequestIdRef.current += 1;
      assetDevices = selectedAsset.devices;
      setDevices(assetDevices);
    } else if (queryFilter.assetId) {
      assetDevices = await syncDevicesForAsset(
        selectedAsset?.aliasId || selectedAsset?.assetId || queryFilter.assetId,
      );
    }

    const deviceTypes = assetDevices?.map((item) =>
      item.deviceType.name.toLocaleLowerCase(),
    );
    const currentFilter = {
      ...safeCurrentFilter,
      ...(deviceTypes?.length
        ? { deviceType: [...new Set(deviceTypes)].join(',') }
        : {}),
    };

    setFilter(currentFilter);

    const reportPromise = getLogReport(currentFilter);
    fetchEngineEvents(currentFilter);
    fetchClutchEvents(currentFilter);
    await reportPromise;
  }

  const queryDeviceType = searchParams.get("deviceType");
  const queryAssetId = searchParams.get("assetId");
  const queryStartAt = searchParams.get("startAt");
  const queryEndAt = searchParams.get("endAt");
  const queryRange = Boolean(queryStartAt && queryEndAt);
  const searchParamsKey = searchParams.toString();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (init) {
      if (
        !isCustomDeviceMode &&
        queryAssetId &&
        (queryDeviceType || queryRange)
      ) {
        // console.log('meredith - fetch data from query');
        handleFetchDataFromQuery();
      } else {
        // console.log('meredith - fetch data init');
        handleFetchInitData();
      }
    }
  }, [init, isCustomDeviceMode, queryAssetId, queryDeviceType, queryRange]);

  return (
    <FilterReportFrame $variant={variant}>
      <Form form={form} name='profileForm' layout='vertical' autoComplete='off'>
      <div className='filter-report-grid' style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {!hideFilters?.includes(EFilterItem.company) && (
          <div className='filter-report-item filter-report-item-company' style={{ flex: 1, maxWidth: 220 }}>
            <Form.Item label='Company' className='label-filter' name='company'>
              <Select
                disabled={loadingCompany}
                loading={loadingCompany}
                showSearch
                placeholder='Choose company'
                style={{ width: '100%' }}
                onChange={handleOnChangeCompany}
                options={filterCompanyOptions}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>
        )}

        {!hideFilters?.includes(EFilterItem.asset) && (
          <div className='filter-report-item filter-report-item-asset' style={{ flex: 1, maxWidth: 220 }}>
            <Form.Item className='label-filter' label='Asset' name='asset'>
              <Select
                disabled={loadingAsset || !filterAssetOptions.length}
                loading={loadingAsset}
                showSearch
                placeholder='Choose asset'
                style={{ width: '100%' }}
                onChange={handleOnChangeAsset}
                options={filterAssetOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>
        )}
        {/* <Col span={3}>
          <Form.Item
            className='label-filter'
            label='Range Type'
            name='rangeType'
          >
            <Select
              showSearch
              placeholder='Choose range type'
              style={{ width: '100%' }}
              onChange={handleRangeTypeChange}
              options={[
                {
                  label: 'Last 30 Days',
                  value: ELogFilterRangeType.last_30_day,
                },
                {
                  label: 'Last Month',
                  value: ELogFilterRangeType.last_month,
                },
                {
                  label: 'Last 7 Days',
                  value: ELogFilterRangeType.last_7_day,
                },
                {
                  label: 'Today',
                  value: ELogFilterRangeType.today,
                },
                {
                  label: 'Last Day',
                  value: ELogFilterRangeType.last_day,
                },
                {
                  label: 'Custom',
                  value: ELogFilterRangeType.custom,
                },
              ]}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col> */}
        <div className='filter-report-item filter-report-item-interval'>
          <Form.Item className='label-filter' label='Interval' name='interval'>
            <Select
              showSearch
              disabled={actionDisabled}
              placeholder='Choose interval'
              style={{ width: '100%' }}
              onChange={handleChangeInterval}
              options={intervalOptions}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </div>
        <div className='filter-report-item filter-report-item-range'>
          <Form.Item className='label-filter' label='Range' name='range'>
            <div className='filter-report-range-picker'>
              <DateRangePicker
                timezone={timezone}
                startAt={filter.startAt}
                endAt={filter.endAt}
                custom={true}
                disabled={actionDisabled}
                onChange={(e) => console.log(e)}
                onChangeCustomTime={handlOnChangeCustomTime}
                onChangeCustomDateRange={handleOnChangeCustomDateRange}
                interval={filter.interval}
                maxRangeDays={
                  minuteOnly
                    ? REPORT_INTERVAL_MAX_DAYS[EReportDataInterval.minute]
                    : MAX_REPORT_RANGE_DAYS
                }
              />
            </div>
          </Form.Item>
        </div>
        <div
          className='filter-report-actions'
          style={{
            alignItems: 'flex-end',
            display: 'flex',
            gap: 4,
          }}
        >
          <Button
            disabled={actionDisabled || (!filter.assetId && !filter.deviceType)}
            loading={loadingLog}
            onClick={async () => {
              await onApplyFilter();
              onApply?.();
            }}
            type='primary'
            color='default'
          >
            Apply
          </Button>
          <ExportDataReport
            columnsFiltered={[]}
            dataSource={[]}
            loading={loadingLog}
          />
          {downloadButton && (
            <div className='filter-report-download-action'>{downloadButton}</div>
          )}
        </div>
      </div>
      </Form>
    </FilterReportFrame>
  );
};

const FilterReportFrame = styled.div<{ $variant: 'default' | 'bottomSheet' }>`
  ${({ $variant }) =>
    $variant === 'bottomSheet' &&
    css`
      width: 100%;
      background: #ffffff;

      .filter-report-grid {
        display: grid !important;
        grid-template-columns: minmax(0, 214px) minmax(0, 1fr);
        column-gap: 8px !important;
        row-gap: 0 !important;
        padding: 12px 12px 0;
      }

      .filter-report-item {
        width: 100% !important;
        max-width: none !important;
        min-width: 0;
        background: #ffffff;
      }

      .filter-report-item-interval,
      .filter-report-item-range,
      .filter-report-actions {
        grid-column: 1 / -1;
      }

      .filter-report-grid .ant-form-item {
        margin-bottom: 0;
      }

      .filter-report-grid .ant-form-item-row {
        display: block;
      }

      .label-filter {
        margin-bottom: 0;
      }

      .label-filter .ant-form-item-label {
        padding-bottom: 3px;
      }

      .label-filter label {
        height: 22px;
        color: #768499;
        font-family: Inter, Poppins, sans-serif;
        font-size: 14px;
        font-weight: 600;
        line-height: 22px;
      }

      .ant-select {
        width: 100%;
      }

      .ant-select-selector {
        display: flex;
        align-items: center;
        height: 44px !important;
        border-color: #e8ecf3 !important;
        border-radius: 6px !important;
        padding: 0 8px !important;
        box-shadow: none !important;
      }

      .ant-select-selection-item,
      .ant-select-selection-placeholder {
        overflow: hidden;
        color: #1e1e1e;
        font-family: Inter, Poppins, sans-serif;
        font-size: 12px;
        line-height: 1.2 !important;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .ant-select-selection-placeholder {
        color: #9facbf;
      }

      .ant-select-selection-search-input {
        height: 42px !important;
      }

      .ant-select-arrow {
        right: 8px;
        color: #9facbf;
      }

      .filter-report-range-picker > div > div {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 14px minmax(0, 1fr);
        align-items: center;
        height: 48px !important;
        padding: 0 8px;
        border-color: #e8ecf3 !important;
        border-radius: 6px !important;
        background: #ffffff !important;
        color: #1e1e1e !important;
        font-family: Inter, Poppins, sans-serif;
        font-size: 12px !important;
        line-height: 1.2 !important;
        overflow: hidden;
      }

      .filter-report-range-picker > div > div[disabled] {
        background: #f4f6f9 !important;
        color: #9facbf !important;
      }

      .filter-report-range-picker > div > div > * {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .filter-report-range-picker .anticon {
        color: #9facbf;
        font-size: 14px;
      }

      .filter-report-actions {
        display: flex !important;
        gap: 8px !important;
        align-items: end !important;
        justify-content: center;
        padding: 12px 0;
      }

      .filter-report-actions .ant-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        height: 44px;
        min-height: 44px;
        border-radius: 6px;
        background: #264284;
        border-color: #264284;
        color: #ffffff;
        font-family: Inter, Poppins, sans-serif;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.2;
        letter-spacing: -0.12px;
        box-shadow: none;
      }

      .filter-report-actions > .ant-btn {
        flex: 1 1 0;
        min-width: 0;
        padding: 0 16px;
      }

      .filter-report-actions .ant-popover-open,
      .filter-report-actions .ant-popover-open .ant-btn {
        background: #264284;
        border-color: #264284;
      }

      .filter-report-actions .ant-btn-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin-inline-end: 0;
      }

      .filter-report-actions .anticon {
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin: 0 !important;
        color: #ffffff;
        font-size: 16px;
        line-height: 1;
      }

      .filter-report-download-action,
      .filter-report-download-action .ant-popover,
      .filter-report-download-action .ant-btn,
      .filter-report-download-action .ant-popover-disabled-compatible-wrapper,
      .filter-report-download-action .ant-popover-disabled-compatible-wrapper .ant-btn {
        flex: 0 0 64px;
        width: 64px;
      }

      .filter-report-download-action .ant-btn {
        position: relative;
        padding: 0 20px;
      }

      .filter-report-download-action .ant-btn > span:not(.ant-btn-icon):not(.anticon) {
        display: none;
      }

      .filter-report-download-action .ant-btn:not(.ant-btn-loading) .anticon,
      .filter-report-download-action .ant-btn:not(.ant-btn-loading) .ant-btn-icon {
        display: none !important;
      }

      .filter-report-download-action .ant-btn:not(.ant-btn-loading)::before {
        content: '';
        display: block;
        width: 24px;
        height: 24px;
        background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 5V14.25' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M8.75 11.25L12 14.5L15.25 11.25' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M7 18H17' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 24px 24px;
      }

      @media (max-width: 360px) {
        .filter-report-grid {
          grid-template-columns: minmax(0, 1fr);
        }
      }
    `}
`;

export default FilterReport;
